import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Ban, Check, Clock, X } from "lucide-react";
import { api, formatApiError } from "../../api";
import { MEMBER } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { useCelebration } from "../../contexts/CelebrationContext";
import { Button } from "../../components/ui/button";
import { toast } from "../../components/ui/sonner";
import { GymLoader } from "../../components/common/GymLoader";

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
}

// UX-MEM-01 §1 SCR-P03-02: class bookings only this phase — there's no provider/resource
// directory a member could browse for PT/resource booking (same documented gap UX-FD-03's
// Roster/Performance screens already hit), so this is a complete, real implementation of exactly
// what's browsable, not a partial one. Capacity/eligibility are both real (`booking_engine`).
export default function Book() {
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const { celebrate } = useCelebration();

  const scheduleQ = useQuery({ queryKey: ["member-schedule"], queryFn: async () => (await api.get("/member/schedule")).data.schedules });
  const bookingsQ = useQuery({ queryKey: ["member-bookings"], queryFn: async () => (await api.get("/member/bookings")).data.bookings });

  const bookMutation = useMutation({
    mutationFn: (scheduleId) => api.post("/member/bookings", { schedule_id: scheduleId }),
    onSuccess: (res) => {
      const waitlisted = res.data?.status === "waitlisted";
      if (waitlisted) {
        toast.success("You're on the waitlist!", { description: "We'll bump you up automatically if a spot opens." });
      } else {
        celebrate("class_booked", "See you on the mat.");
      }
      queryClient.invalidateQueries({ queryKey: ["member-schedule"] });
      queryClient.invalidateQueries({ queryKey: ["member-bookings"] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });
  const cancelMutation = useMutation({
    mutationFn: (bookingId) => api.post(`/member/bookings/${bookingId}/cancel`, {}),
    onSuccess: () => {
      toast.success("Booking cancelled.");
      queryClient.invalidateQueries({ queryKey: ["member-schedule"] });
      queryClient.invalidateQueries({ queryKey: ["member-bookings"] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const activeBookingBySchedule = new Map((bookingsQ.data || []).filter((b) => b.status === "booked" || b.status === "waitlisted").map((b) => [b.target_ref, b]));

  return (
    <div data-testid={MEMBER.bookPage} className="space-y-3">
      <motion.h1 {...fadeUp(reducedMotion)} className="font-heading text-2xl font-bold mb-2">Today's classes</motion.h1>

      {scheduleQ.isLoading && <GymLoader label="Loading classes…" testId="member-book-loader" />}
      {scheduleQ.data?.length === 0 && <p className="text-sm" style={{ color: "#A1A1AA" }}>No classes scheduled right now.</p>}

      {(scheduleQ.data || []).map((s, i) => {
        const myBooking = activeBookingBySchedule.get(s.id);
        return (
          <motion.div key={s.id} {...fadeUp(reducedMotion, 0.03 * i)} data-testid={MEMBER.classRow(s.id)} className="rounded-2xl p-4 flex items-center gap-4" style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}>
            <div className="text-center shrink-0 w-14">
              <p className="text-xs" style={{ color: "#A1A1AA" }}><Clock size={11} className="inline mr-0.5" />{fmtTime(s.start)}</p>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold capitalize truncate">{s.class_type}</p>
              {!s.eligible && <p className="text-xs mt-0.5" style={{ color: "#D6202B" }}>Not covered by your plan</p>}
            </div>
            <span className="text-xs font-semibold px-2 py-1 rounded-full shrink-0" style={{ background: s.full ? "rgba(214, 32, 43,0.15)" : "rgba(50,215,75,0.15)", color: s.full ? "#D6202B" : "#32D74B" }}>
              {s.confirmed_count}/{s.capacity}
            </span>
            {myBooking ? (
              <Button data-testid={MEMBER.classCancelButton(s.id)} size="sm" variant="outline" disabled={cancelMutation.isPending} onClick={() => cancelMutation.mutate(myBooking.id)} className="shrink-0 gap-1 border-white/20 text-white bg-transparent hover:bg-white/10">
                <X size={13} /> Cancel
              </Button>
            ) : (
              <Button
                data-testid={MEMBER.classBookButton(s.id)} size="sm" disabled={(s.full && s.waitlist_full) || !s.eligible || bookMutation.isPending}
                onClick={() => bookMutation.mutate(s.id)} className="shrink-0 text-white active:scale-95 transition-transform gap-1" style={{ background: (s.full && s.waitlist_full) || !s.eligible ? "#242424" : s.full ? "#B45309" : "#D6202B" }}
              >
                {s.full && s.waitlist_full ? <><Ban size={13} /> Full</> : s.full ? <>Join waitlist</> : <><Check size={13} /> Book</>}
              </Button>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}
